/**
 * MasterDataServiceTypeFormDialog — Create/Edit `service_type`.
 * 1:1 port of Access frmMdQryServiceTypeAdd (Master Data > CP List > Service Type).
 *
 * Field order theo layout Access (Top): Service Type Code → Service Type Name →
 * Note. PK `service_type_code` bất biến sau khi tạo → readonly khi Edit (khớp VBA
 * btnSave: nhánh `service_type_code.Enabled = False` = UPDATE name/note). BE lưu
 * code = lowercase.
 */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdServiceTypeMutation,
  useUpdateMdServiceTypeMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataServiceType } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataServiceType | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  service_type_code: string
  service_type_name: string
  note: string
}

const EMPTY: FormState = {
  service_type_code: '',
  service_type_name: '',
  note: '',
}

export default function MasterDataServiceTypeFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createServiceType, { isLoading: creating }] = useCreateMdServiceTypeMutation()
  const [updateServiceType, { isLoading: updating }] = useUpdateMdServiceTypeMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTouched(false)
    if (isEdit && initialValue) {
      setForm({
        service_type_code: initialValue.service_type_code ?? '',
        service_type_name: initialValue.service_type_name ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.service_type_code.trim()
  const canSave = isEdit || !codeEmpty

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (!isEdit && codeEmpty) {
      setTouched(true)
      return
    }
    if (loading) return
    try {
      if (isEdit && initialValue) {
        await updateServiceType({
          code: initialValue.service_type_code,
          body: {
            service_type_name: form.service_type_name.trim() || null,
            note: form.note.trim() || null,
          },
        }).unwrap()
        snack.success('Service Type updated')
      } else {
        await createServiceType({
          service_type_code: form.service_type_code.trim(),
          service_type_name: form.service_type_name.trim() || null,
          note: form.note.trim() || null,
        }).unwrap()
        snack.success('Service Type created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Service Type' : 'New Service Type'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.service_type_code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave || loading}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 1.5, mt: 0.5 }}>
          {/* Service Type Code — PK, readonly khi Edit. */}
          <TextField
            label="Service Type Code"
            required
            size="small"
            fullWidth
            value={form.service_type_code}
            onChange={(e) => set('service_type_code', e.target.value)}
            disabled={isEdit}
            error={!isEdit && touched && codeEmpty}
            InputLabelProps={{ shrink: true }}
            InputProps={isEdit ? { readOnly: true } : undefined}
            inputProps={{ tabIndex: isEdit ? -1 : 1 }}
            sx={isEdit ? roSx : undefined}
            helperText={isEdit ? 'Cannot be changed after creation.' : undefined}
          />
          <TextField
            label="Service Type Name"
            size="small"
            fullWidth
            value={form.service_type_name}
            onChange={(e) => set('service_type_name', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: 2 }}
          />
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: 3 }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
